/**
 * Utilitaire pour gérer le smooth scroll sur tous les navigateurs,
 * y compris ceux qui désactivent scroll-behavior: smooth (comme Brave)
 */

// Déclaration TypeScript pour l'API Brave
declare global {
  interface Navigator {
    brave?: {
      isBrave: () => Promise<boolean>;
    };
  }
}

export const smoothScrollTo = (element: HTMLElement | null, offset: number = 0) => {
  if (!element) return;

  const targetPosition = element.getBoundingClientRect().top + window.pageYOffset - offset;
  
  // Vérifier si le navigateur supporte scroll-behavior
  if ('scrollBehavior' in document.documentElement.style) {
    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });
  } else {
    // Fallback animé pour les navigateurs qui ne supportent pas scroll-behavior
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const duration = 800; // ms
    let startTime: number | null = null;

    const animation = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      // Easing function (easeInOutCubic)
      const ease = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      window.scrollTo(0, startPosition + distance * ease);

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  }
};

/**
 * Alternative avec détection de Brave
 */
export const isBrave = async (): Promise<boolean> => {
  return (navigator.brave && await navigator.brave.isBrave()) || false;
};

/**
 * Configure le smooth scroll en fonction du navigateur
 */
export const setupSmoothScroll = () => {
  // Intercepter tous les liens d'ancrage
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (this: HTMLAnchorElement, e: Event) {
      e.preventDefault();
      const href = this.getAttribute('href');
      if (href && href !== '#') {
        const targetElement = document.querySelector(href);
        if (targetElement) {
          smoothScrollTo(targetElement as HTMLElement, 80); // 80px offset pour le header
        }
      }
    });
  });
};
